/**
 * FIN-01 §4's tree, as pure functions over the flat code-ordered list the API
 * serves. No React here — the chart page renders what these return, and the
 * unit tests exercise them directly.
 */

export interface TreeSource {
  id: string;
  code: string;
  parentId: string | null;
}

export interface TreeNode<T extends TreeSource> {
  account: T;
  depth: number;
  children: TreeNode<T>[];
}

/**
 * Builds the nested tree in code order (FIN-01: "sorting and grouping follow
 * the code"). An orphan (parent missing — half-loaded data) lands at the
 * root rather than disappearing.
 */
export function buildAccountTree<T extends TreeSource>(accounts: readonly T[]): TreeNode<T>[] {
  const nodes = new Map<string, TreeNode<T>>();
  for (const account of accounts) {
    nodes.set(account.id, { account, depth: 0, children: [] });
  }
  const roots: TreeNode<T>[] = [];
  for (const account of accounts) {
    const node = nodes.get(account.id)!;
    const parent = account.parentId !== null ? nodes.get(account.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const stamp = (list: TreeNode<T>[], depth: number) => {
    for (const node of list) {
      node.depth = depth;
      stamp(node.children, depth + 1);
    }
  };
  stamp(roots, 0);
  return roots;
}

/** Depth-first flatten, honoring a set of collapsed branch ids. */
export function flattenTree<T extends TreeSource>(
  roots: readonly TreeNode<T>[],
  collapsed: ReadonlySet<string>,
): TreeNode<T>[] {
  const out: TreeNode<T>[] = [];
  const walk = (nodes: readonly TreeNode<T>[]) => {
    for (const node of nodes) {
      out.push(node);
      if (!collapsed.has(node.account.id)) walk(node.children);
    }
  };
  walk(roots);
  return out;
}

/**
 * BR-FIN-01-04's picker half: the account itself and every account beneath
 * it, at any depth — the set the parent list must EXCLUDE (ERR-FIN-01-05:
 * "the option is not offered at all").
 */
export function selfAndDescendants(accounts: readonly TreeSource[], accountId: string): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const account of accounts) {
    if (account.parentId === null) continue;
    const list = childrenOf.get(account.parentId) ?? [];
    list.push(account.id);
    childrenOf.set(account.parentId, list);
  }
  const excluded = new Set<string>();
  const queue = [accountId];
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (excluded.has(id)) continue;
    excluded.add(id);
    for (const child of childrenOf.get(id) ?? []) queue.push(child);
  }
  return excluded;
}

/**
 * BR-FIN-01-08: the TREE shows figures without a sign; only the detail card
 * exposes the sign (in red). "-14500.00" → "14500.00".
 */
export function unsigned(balance: string): string {
  return balance.startsWith('-') ? balance.slice(1) : balance;
}
